'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { dictionaries, Locale, SUPPORTED_LANGUAGES } from '@/i18n/dictionaries';
import { ChevronRight } from 'lucide-react';

export type { Locale };
export { SUPPORTED_LANGUAGES };

type LanguageContextType = {
  locale: Locale;
  setLocale: (lang: Locale) => void;
  t: typeof dictionaries['ru'];
  currentLabel: string;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: React.ReactNode;
  initialLocale: Locale;
}) {
  const [locale, setLocaleState] = useState(initialLocale);
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    setLocaleState(initialLocale);
  }, [initialLocale]);

  const setLocale = (newLang: Locale) => {
    if (newLang === locale) return;
    setLocaleState(newLang);

    const ONE_YEAR = 31536000;
    document.cookie = `app_locale=${newLang}; path=/; max-age=${ONE_YEAR}; samesite=lax`;

    const segments = window.location.pathname.split('/');
    if (segments.length > 1 && (segments[1] === 'ru' || segments[1] === 'en')) {
      segments[1] = newLang;
    } else {
      segments.splice(1, 0, newLang);
    }

    const currentSearchParams = searchParams.toString();
    let cleanPath = segments.join('/').replace(/\/$/, '');
    if (!cleanPath) cleanPath = '/';

    const newPath = cleanPath + (currentSearchParams ? `?${currentSearchParams}` : '');

    router.push(newPath);
    router.refresh(); 
  };

  const currentLabel = SUPPORTED_LANGUAGES.find((l) => l.code === locale)?.label || 'Русский';

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t: dictionaries[locale], currentLabel }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// ==========================================
// СЕЛЕКТОР ЯЗЫКОВ (БЕЛЫЙ ТЕКСТ)
// ==========================================
export function LanguageSelector() {
  const { locale, setLocale, currentLabel } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    setLocale(code as Locale);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block select-none" ref={containerRef}>
      {/* Кнопка-триггер: Белый текст вместо серого */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-[17px] font-medium text-white hover:opacity-75 transition-opacity duration-200 outline-none cursor-pointer bg-transparent border-none p-0 group"
      >
        <span>
          {currentLabel}
        </span>
        
        <ChevronRight 
          size={18} 
          className={`transition-transform duration-300 text-white ${
            isOpen ? 'rotate-90' : 'rotate-0'
          }`}
        />
      </button>

      {/* Выпадающее меню */}
      <div
        className={`
          absolute top-full right-0 mt-3 w-36 bg-[#0c0c0c] border border-[#1f1f1f] rounded-2xl shadow-2xl p-1.5 z-50 origin-top-right transition-all duration-200
          ${isOpen ? 'opacity-100 scale-100 translate-y-0 visible' : 'opacity-0 scale-95 -translate-y-2 invisible'}
        `}
      >
        <div className="flex flex-col gap-1">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected = lang.code === locale;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleSelect(lang.code)}
                className={`
                  w-full text-left px-3 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer flex items-center justify-between
                  ${
                    isSelected
                      ? 'bg-[#18181b] text-white font-semibold'
                      : 'text-zinc-400 hover:text-white hover:bg-[#151515]'
                  }
                `}
              >
                <span>{lang.label}</span>
                {isSelected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_#6366f1]" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}